import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import ProductCard from '../components/ProductCard';
import { products } from '../data/products';
import { useLanguage } from '../context/LanguageContext';
import { SPRING_DEFAULT, REDUCED_MOTION_TRANSITION } from '../utils/springs';
import './Products.css';

const Products = () => {
  const [searchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('default');
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const { t } = useLanguage();
  const reduceMotion = useReducedMotion();
  const gridTransition = reduceMotion ? REDUCED_MOTION_TRANSITION : SPRING_DEFAULT;

  const category = searchParams.get('category');
  const bestseller = searchParams.get('bestseller') === 'true';
  const isNew = searchParams.get('new') === 'true';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleHashChange = () => {
      if (window.location.hash === '#products-page-search') {
        searchInputRef.current?.focus();
      }
    };

    // Focus on mount if hash is present
    handleHashChange();

    // Focus on hash change
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((p) => {
        const name = p.name.toLowerCase();
        const nameRo = p.nameRo?.toLowerCase() || '';
        const description = p.description?.toLowerCase() || '';
        const descriptionRo = p.descriptionRo?.toLowerCase() || '';
        return (
          name.includes(term) ||
          nameRo.includes(term) ||
          description.includes(term) ||
          descriptionRo.includes(term)
        );
      });
    }

    if (category) {
      filtered = filtered.filter((p) => p.category === category);
    }

    if (bestseller) {
      filtered = filtered.filter((p) => p.bestseller);
    }

    if (isNew) {
      filtered = filtered.filter((p) => p.new);
    }

    const sorted = [...filtered];
    const sortLast = (a, b) => {
      const aLast = a.comingSoon || a.price == null;
      const bLast = b.comingSoon || b.price == null;
      if (aLast !== bLast) {
        return aLast ? 1 : -1;
      }
      return 0;
    };
    if (sortBy === 'price-asc') {
      sorted.sort((a, b) => {
        const gate = sortLast(a, b);
        if (gate !== 0) return gate;
        return a.price - b.price;
      });
    } else if (sortBy === 'price-desc') {
      sorted.sort((a, b) => {
        const gate = sortLast(a, b);
        if (gate !== 0) return gate;
        return b.price - a.price;
      });
    } else if (sortBy === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }

    return sorted;
  }, [category, bestseller, isNew, sortBy, searchTerm]);

  const getPageTitle = () => {
    if (bestseller) return t('productsPage.bestsellers');
    if (isNew) return t('productsPage.newProducts');
    if (category === 'hazelnuts') return t('productsPage.hazelnuts');
    if (category === 'snacks') return t('productsPage.snacks');
    if (category === 'truffles') return t('productsPage.truffles');
    return t('productsPage.allProducts');
  };

  return (
    <div className="products-page">
      <div className="container">
        <h1 className="page-title">{getPageTitle()}</h1>

        <div className="search-bar">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
          <input
            ref={searchInputRef}
            id="products-page-search"
            type="search"
            placeholder={t('productsPage.searchPlaceholder')}
            aria-label={t('productsPage.searchAria')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="search-clear"
              aria-label={t('productsPage.searchClear')}
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>

        <div className="products-controls">
          <div className="products-info">
            <span>
              {filteredProducts.length} {t('productsPage.products')}
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
              aria-label={t('productsPage.filterSort')}
            >
              <option value="default">{t('productsPage.defaultSort')}</option>
              <option value="name">{t('productsPage.sortName')}</option>
              <option value="price-asc">{t('productsPage.priceLowHigh')}</option>
              <option value="price-desc">{t('productsPage.priceHighLow')}</option>
            </select>
          </div>
        </div>

        <motion.div className="products-grid" layout={!reduceMotion ? 'position' : false}>
          {filteredProducts.length > 0 ? (
            <AnimatePresence mode="popLayout">
              {filteredProducts.map((product) => (
                <motion.div
                  key={product.id}
                  layout={!reduceMotion ? 'position' : false}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={gridTransition}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            <div className="no-products">
              <p>{t('productsPage.noProducts')}</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Products;
